/**
 * WizardShell — the frame used by both BootcampQuizFlow and BenchmarkWizard.
 *
 * Layout (mobile-first, matching the reference screenshots)
 * --------------------------------------------------------
 * ┌─ back arrow (top-left) ──────────────────────────────────┐
 * │                                                          │
 * │     [<]      Question X of Y                             │
 * │     ▂▂▂▂▂▂▂▂▂▂▂▂░░░░░░░░░░░  (progress bar)              │
 * │                                                          │
 * │                   HUGE QUESTION TITLE                    │
 * │                   muted helper subtitle                  │
 * │                                                          │
 * │                   (slot for the answer widget)           │
 * │                                                          │
 * │ ──────────────────────────────────────────────────────── │
 * │              [      Continue →       ]  (full-width)     │
 * └──────────────────────────────────────────────────────────┘
 *
 * Keyboard
 * --------
 * - ArrowLeft → previous
 * - ArrowRight / Enter → next (or submit on the last question)
 * These are wired on the shell so every question type inherits them.
 *
 * Accessibility
 * -------------
 * - Progress bar is a proper `role="progressbar"` via <ProgressBar>.
 * - aria-live polite region announces the step change to screen readers.
 * - Every button is a <Button> primitive — focus-visible rings and
 *   44px touch targets inherit from the design system.
 *
 * Desktop
 * -------
 * The shell is max-width 640px centred; on mobile it fills the width.
 * Parent components mount this inside a Modal (for the quiz) or inline
 * (for the benchmark) so the chrome adapts to context.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Check, CornerDownLeft, Sparkles } from 'lucide-react';
import Button from '../../ui/Button';
import ProgressBar from '../../ui/ProgressBar';
import './WizardShell.css';

export type WizardShellProps = {
    /** 1-indexed current step */
    step: number;
    /** total number of steps */
    totalSteps: number;
    /** main question text — renders huge */
    title: string;
    /** helper subtitle (muted, small) */
    subtitle?: string;
    /** the answer widget for the current step */
    children: ReactNode;
    /** whether the user has provided a valid answer for this step */
    canContinue: boolean;
    /** back arrow callback. null disables the back arrow (first step). */
    onBack: (() => void) | null;
    /** continue / next callback */
    onContinue: () => void;
    /** called when the user exits entirely (close button or back from step 1) */
    onExit: () => void;
    /** label for the continue button — defaults to "Continue" / on last step "Submit" */
    continueLabel?: string;
    /** show the sparkle CTA variant ("Generate My Roadmap") on the last step */
    finalSparkle?: boolean;
    /** when true, the continue button shows a saving state */
    busy?: boolean;
};

function WizardShell({
    step,
    totalSteps,
    title,
    subtitle,
    children,
    canContinue,
    onBack,
    onContinue,
    onExit,
    continueLabel,
    finalSparkle = false,
    busy = false,
}: WizardShellProps) {
    const isLast = step === totalSteps;
    const pct = (step / totalSteps) * 100;
    const label = continueLabel ?? (isLast ? (finalSparkle ? 'Generate my roadmap' : 'Submit') : 'Continue');

    // Track slide direction so the CSS transition differs between
    // "next" and "prev". We keep a ref of the previous step and update
    // it from the navigation handlers (not from an effect) so the React
    // 19 hook linter stays happy.
    const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

    // Auto-focus the first interactive element inside the answer slot
    // whenever the step changes. Prefers textareas/inputs (so the user
    // can start typing immediately) and falls back to the first
    // role=radio option.
    const answerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const root = answerRef.current;
        if (!root) return;
        // Delay by one frame so the slide-in animation starts from the
        // right position before we move focus.
        const id = window.requestAnimationFrame(() => {
            const preferred = root.querySelector<HTMLElement>(
                'textarea, input[type="text"], input[type="number"], input[type="range"]',
            );
            if (preferred) {
                preferred.focus({ preventScroll: true });
                return;
            }
            const firstRadio = root.querySelector<HTMLElement>('[role="radio"]');
            firstRadio?.focus({ preventScroll: true });
        });
        return () => window.cancelAnimationFrame(id);
    }, [step]);

    // "Press Enter to continue" hint — shown on the first answered step,
    // then auto-hides forever so it doesn't nag.
    const [hintSeen, setHintSeen] = useState(false);
    const showHint = canContinue && !busy && !hintSeen && step === 1;

    const handleContinueWrapped = useCallback(() => {
        if (!hintSeen) setHintSeen(true);
        setDirection('forward');
        onContinue();
    }, [hintSeen, onContinue]);

    const handleBackWrapped = useCallback(() => {
        if (!onBack) return;
        setDirection('backward');
        onBack();
    }, [onBack]);

    // Keyboard shortcuts: ArrowLeft = back, ArrowRight/Enter = next.
    // Ignored while focus is inside a text input so typing answers isn't
    // intercepted — except Enter in a single-line input, which commits.
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                if (e.key === 'Enter' && target.tagName === 'INPUT' && canContinue && !busy) {
                    e.preventDefault();
                    handleContinueWrapped();
                }
                return;
            }
            if (e.key === 'ArrowLeft' && onBack) {
                e.preventDefault();
                handleBackWrapped();
            } else if ((e.key === 'ArrowRight' || e.key === 'Enter') && canContinue && !busy) {
                e.preventDefault();
                handleContinueWrapped();
            }
        },
        [busy, canContinue, onBack, handleBackWrapped, handleContinueWrapped],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <div className="wizard-shell">
            <div className="wizard-shell__topbar">
                <Button
                    variant="ghost"
                    size="sm"
                    icon={<ArrowLeft size={18} />}
                    onClick={onBack ? handleBackWrapped : onExit}
                    aria-label={onBack ? 'Previous question' : 'Exit wizard'}
                    className="wizard-shell__back"
                />
                <div className="wizard-shell__progress">
                    <p className="wizard-shell__step-label" aria-live="polite">
                        Question {step} of {totalSteps}
                    </p>
                    <ProgressBar value={pct} ariaLabel={`Step ${step} of ${totalSteps}`} />
                </div>
            </div>

            <div
                className={`wizard-shell__body wizard-shell__body--${direction}`}
                // key forces React to remount the slide content on step change
                // which re-triggers the CSS enter animation.
                key={step}
            >
                <h2 className="wizard-shell__title">{title}</h2>
                {subtitle && <p className="wizard-shell__subtitle">{subtitle}</p>}

                <div className="wizard-shell__answer" ref={answerRef}>
                    {children}
                </div>
            </div>

            <div className="wizard-shell__footer">
                <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={handleContinueWrapped}
                    disabled={!canContinue || busy}
                    icon={
                        isLast && finalSparkle ? <Sparkles size={18} /> :
                            isLast ? <Check size={18} /> :
                                <ArrowRight size={18} />
                    }
                    iconPosition="right"
                    className={`wizard-shell__cta${canContinue && !busy ? ' wizard-shell__cta--ready' : ''}`}
                >
                    {busy ? 'Saving…' : label}
                </Button>
                {showHint && (
                    <p className="wizard-shell__hint" aria-hidden="true">
                        <CornerDownLeft size={12} /> Press Enter to continue
                    </p>
                )}
            </div>
        </div>
    );
}

export default WizardShell;
